import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useApi } from "@/"; // Replace with your actual hook
import { useSelector } from "react-redux"; // Import useSelector from react-redux

const clientId = import.meta.env.VITE_SHOPIFY_CLIENT_ID;
const scopes = "read_orders,write_orders,read_all_orders";
const redirectUri = "https://app.indiancouriercompany.com/settings/channel";

// Helper function to check if a string is a valid MongoDB ObjectId
const isValidObjectId = (id) => {
  return id && /^[0-9a-fA-F]{24}$/.test(id);
};

export default function ShopifyOAuthHandler({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { callApi } = useApi();
  const hasProcessed = useRef(false);

  // Get user ID from Redux store
  const user = useSelector((state) => state.user?.user);
  const userId = user?._id;

  const shop = searchParams.get("shop");
  const code = searchParams.get("code");
  const hmac = searchParams.get("hmac");
  const state = searchParams.get("state");

  useEffect(() => {
    // Parse the state parameter if it exists
    let stateObj = null;
    console.log("state string:", state);

    if (state) {
      try {
        // First try to decode the state if it's URL-encoded
        const decodedState = decodeURIComponent(state);
        console.log("decoded state:", decodedState);

        // Check if the state starts with '{' which indicates it's a JSON string
        if (decodedState.startsWith("{")) {
          stateObj = JSON.parse(decodedState);
        } else {
          // If it doesn't start with '{', it might be a different format
          // Try to extract the JSON part if it's in a format like: {"userId":"user-123","nonce":"5z5v4ofbtpo"}
          const jsonMatch = decodedState.match(/\{.*\}/);
          if (jsonMatch) {
            stateObj = JSON.parse(jsonMatch[0]);
          }
        }

        console.log("parsed state object:", stateObj);
        console.log("userId from state:", stateObj?.userId || "N/A UserID");
      } catch (e) {
        console.error("Invalid state parameter:", e);
      }
    }

    // Check if we have a userId in the state that is NOT a valid MongoDB ObjectId
    if (
      shop &&
      stateObj &&
      stateObj.userId &&
      !isValidObjectId(stateObj.userId)
    ) {
      // If userId exists but is NOT a valid MongoDB ObjectId, return early
      console.log("Returning early: userId is not a valid MongoDB ObjectId");
      navigate("/login");
      return;
    }

    if (shop && !code) {
      const state = JSON.stringify({
        userId: userId, // Get userId from Redux store
        nonce: Math.random().toString(36).substring(2),
      });

      const installUrl =
        `https://${shop}/admin/oauth/authorize` +
        `?client_id=${clientId}` +
        `&scope=${scopes}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&state=${encodeURIComponent(state)}`;

      window.location.href = installUrl;
      return;
    }

    if (shop && code && hmac && !hasProcessed.current) {
      hasProcessed.current = true;

      (async () => {
        const params = Object.fromEntries(searchParams.entries());

        try {
          const response = await callApi(
            "/channel-integration/shopify/auth/callback",
            "POST",
            {
              ...params,
              refreshTokenUrl:
                `https://${shop}/admin/oauth/authorize` +
                `?client_id=${clientId}` +
                `&scope=${scopes}` +
                `&redirect_uri=${encodeURIComponent(redirectUri)}`,
            }
          );

          if (response.success) {
            setSearchParams({});
            navigate("/settings/channel");
          } else {
            throw new Error(response.error || "OAuth failed");
          }
        } catch (err) {
          console.error("OAuth error:", err);
          //alert("Failed to connect Shopify store: " + err.message);
        }
      })();
    }
  }, [shop, code, hmac, state]);

  return <>{children}</>;
}
